import React from 'react';
import filter from 'lodash/filter';
import styled from 'styled-components';
import uniq from 'lodash/uniq';
import { useSelector } from 'react-redux';

import ConnectorWrapper from 'src/presentation/components/pages/sidekick/threads/components/Connectors/ConnectorWrapper';
import DraggableModifiers from './DraggableModifiers';
import ErrorComponent from 'src/presentation/components/pages/sidekick/threads/components/ErrorComponent';
import ExpandCollapseButton from 'src/presentation/components/pages/sidekick/threads/components/ExpandCollapseButton';
import FocusedContainer from 'src/presentation/components/pages/sidekick/threads/components/FocusedContainer';
import { getModuleSpec } from 'src/presentation/components/pages/sidekick/threads/utils/utils';
import { ModifierValue, NextData } from 'src/models/Sidekick/SidekickThreads';
import ModuleActions, { ModuleActionsContainer } from './ModuleActions';
import AddModule from 'src/presentation/components/pages/sidekick/threads/components/AddModule';
import {
  selectAnon,
  selectCurrentAnonModifier,
  selectCurrentAnonModule,
  selectCurrentModifier,
  selectCurrentModule,
  selectErrors,
  selectModuleSpecsMap,
} from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import { StyledActionText } from 'src/presentation/components/pages/sidekick/threads/SidekickLayout';
import {
  HIDE_SUB_ACTION_LABEL,
  SHOW_SUB_ACTION_LABEL,
} from 'src/presentation/components/pages/sidekick/threads/SidekickConstants';
import useExpandableModule from 'src/presentation/components/pages/sidekick/threads/hooks/useExpandableModule';

import { ActionsSpecs } from '../../SidekickActionsConstants';

export type ModuleElementRef = {
  isOpen: boolean;
  expand: () => void;
  collapse: () => void;
};

type ModuleComponentProps = {
  attr: string;
  disabled: boolean;
  item: NextData;
  provided: any;
  snapshot: any;
  handleClick: (ev: React.MouseEvent) => void;
  handleModifierClick: (ev: React.MouseEvent, module: NextData, modifier: ModifierValue) => void;
  showAddModuleButton: boolean;
};

const ModuleComponent = ({
  attr,
  disabled,
  item: module,
  provided,
  snapshot,
  handleClick,
  handleModifierClick,
  showAddModuleButton,
}: ModuleComponentProps) => {
  const currentModule = useSelector(selectCurrentModule);
  const currenModifier = useSelector(selectCurrentModifier);
  const currentAnonModule = useSelector(selectCurrentAnonModule);
  const currentAnonModifier = useSelector(selectCurrentAnonModifier);
  const isAnon = useSelector(selectAnon);

  const errors = useSelector(selectErrors);
  const ownErrors = uniq(filter(errors, error => error.attr.startsWith(attr)).map(error => error.detail)).sort();
  const hasErrors = ownErrors.length > 0;

  const isSelected =
    (isAnon && currentAnonModule?.id === module.id && !currentAnonModifier) ||
    (!isAnon && currentModule?.id === module.id && !currenModifier);

  const moduleSpecsMap = useSelector(selectModuleSpecsMap);

  const hasModifiers = !!module.modifierValues?.length;

  let spec = getModuleSpec(module, moduleSpecsMap);

  const alteredAction = ActionsSpecs[module!.name];
  if (spec) {
    spec = { ...spec, ...alteredAction };
  }

  const { isOpen, toggle } = useExpandableModule({ moduleId: module.id! });

  const name = spec?.presentableName || module.name;

  const Modifiers = ({ item, onModifierClick }) => {
    return (
      <>
        {hasModifiers ? (
          <DraggableModifiers
            attr={`${attr}.modifierValues`}
            disabled={disabled}
            module={item}
            onModifierClick={onModifierClick}
          />
        ) : null}
      </>
    );
  };

  const numberOfModifiers = module.modifierValues.length;
  const meta = { moduleId: module.id! };

  const handleModuleClick = (ev: React.MouseEvent) => {
    ev.preventDefault();
    if (isSelected) {
      return;
    }

    handleClick(ev);
  };

  return (
    <ModuleContainerWithAddButton>
      <ConnectorWrapper connectedPointPosition="right" id={module.id!} type="MODULE" meta={meta}>
        <FocusedContainer>
          <StyledModuleContainer
            ref={provided.innerRef}
            {...provided.draggableProps}
            isSelected={isSelected}
            isExpanded={isOpen && hasModifiers}
            hasErrors={hasErrors}
          >
            <StyledModuleItem
              className="module-item"
              isExpanded={isOpen}
              aria-expanded={isOpen}
              hasModifiers={hasModifiers}
              onClick={handleModuleClick}
              isSelected={isSelected}
              id={module.id}
              isDragging={snapshot.isDragging}
              provided={provided}
              {...provided.dragHandleProps}
            >
              <ExpandCollapseButton
                hasChildren={hasModifiers}
                isOpen={isOpen}
                setIsOpen={toggle}
                title={{ expanded: HIDE_SUB_ACTION_LABEL, collapsed: SHOW_SUB_ACTION_LABEL }}
              />

              <StyledItem>
                {name}
                {!isOpen && numberOfModifiers > 0 && <Badge>{numberOfModifiers}</Badge>}
              </StyledItem>
              <ErrorComponent attr={attr} />
              <ModuleActions className="module-actions" disabled={disabled} moduleId={module.id!} moduleSpec={spec} />
            </StyledModuleItem>
            <Modifiers item={module} onModifierClick={handleModifierClick} />
          </StyledModuleContainer>
        </FocusedContainer>
      </ConnectorWrapper>

      {showAddModuleButton && (
        <AddModule
          aboveModuleId={module.id!}
          className="addModule"
          disabled={disabled}
          moduleSpec={null}
          isVisible={!snapshot.isDragging}
        />
      )}
    </ModuleContainerWithAddButton>
  );
};

const Badge = styled.span`
  background-color: ${p => p.theme.colors.green400};
  padding: 4px 8px;
  margin-left: 8px;

  border-radius: 50%;
  background: var(--Gray-100---RGBA, rgba(0, 0, 0, 0.04));
`;

const ModuleContainerWithAddButton = styled.div`
  background-color: ${p => p.theme.colors.white};
`;

const StyledModuleContainer = styled.div.attrs(p => ({
  isSelected: !!p.isSelected,
  hasErrors: !!p.hasErrors,
}))`
  background-color: ${p => p.theme.colors.white};
  ${p =>
    p.isSelected ? `border: 1px solid ${p.theme.colors.green400};` : `border: 1px solid ${p.theme.colors.gray300}`};
  border-radius: ${p => p.theme.borderRadius.small};
  gap: ${p => p.theme.borderRadius.small};

  ${p => p.isExpanded && 'padding-bottom: 8px;'}
`;

const StyledModuleItem = styled.div.attrs(p => ({
  isSelected: p.isSelected || false,
  isExpanded: p.isExpanded,
  role: 'listitem',
}))`
  ${StyledActionText}
  align-items: center;
  display: flex;
  padding: 8px 12px;
  position: relative;
  height: 50px;

  border-radius: 0.5rem 0.5rem 0 0;

  background-color: ${p => (p.isSelected ? p.theme.colors.green100 : p.theme.colors.white)};

  ${p => p.isSelected && `background-color: ${p.theme.colors.green100};`};

  ${p => !p.hasModifiers && `border-radius: ${p.theme.borderRadius.small}`};
  ${p => !p.isExpanded && p.hasModifiers && `border-radius: ${p.theme.borderRadius.small}`};
  ${p => p.isExpanded && p.isSelected && p.hasModifiers && `border-bottom: 1px solid ${p.theme.colors.green400}`};

  gap: 4px;

  &:hover ${ModuleActionsContainer} {
    visibility: visible;
  }
`;

export const StyledItem = styled.div`
  margin-right: auto;
`;

export default ModuleComponent;
